import React from 'react';
import PropTypes from 'prop-types';
import { Grid } from 'semantic-ui-react';

import { isPetasenseAdmin, isOrgAdmin } from 'common/rbac/util';
import { useSiteReassign } from '../hooks/useSiteReassign';
import SiteReassignField from './SiteReassignField';

function SiteReassignSection({
  user,
  currentAccount,
  editMode,
  getOrganisationSites,
  transferDeviceToSite,
  getOrganisationId,
  getSiteAccountId,
  getTransferDeviceParams,
  hasMachineAssociation,
  getDisabledHint,
  onReassignSuccess,
  t,
}) {
  const orgId = getOrganisationId ? getOrganisationId() : (
    currentAccount
    && currentAccount.organisation
    && currentAccount.organisation.id
  );
  // Checked before the hook, not after: useSiteReassign fetches the org's sites on
  // mount and /organisations/<id>/accounts is org_admin-only, so gating the render
  // alone still 403'd every other role.
  const canReassignSite = isPetasenseAdmin(user) || isOrgAdmin(user);
  const isMachineAssociated = hasMachineAssociation ? hasMachineAssociation() : false;
  // Panels that block for more than a machine assignment supply their own text;
  // otherwise the machine wording still applies.
  const disabledHint = (isMachineAssociated && getDisabledHint && getDisabledHint())
    || t('sensors.detail.siteDisabledHint');

  const {
    siteOptions,
    reassignSubmitting,
    selectValue,
    siteDisplayName,
    hasPendingReassign,
    handleSiteSelectChange,
    handleCancelPendingSite,
    handleConfirmReassign,
  } = useSiteReassign({
    orgId: canReassignSite ? orgId : null,
    getOrganisationSites,
    transferDeviceToSite,
    t,
    getSiteAccountId,
    getTransferDeviceParams,
    hasMachineAssociation,
    disabledHint,
    onReassignSuccess,
  });

  if (!orgId || !canReassignSite) {
    return null;
  }

  return (
    <Grid.Row>
      <Grid.Column>
        <SiteReassignField
          t={t}
          editMode={editMode}
          siteOptions={siteOptions}
          selectValue={selectValue}
          siteDisplayName={siteDisplayName}
          disabled={isMachineAssociated}
          submitting={reassignSubmitting}
          hasPendingReassign={hasPendingReassign}
          onSiteChange={handleSiteSelectChange}
          onConfirmReassign={handleConfirmReassign}
          onCancelPending={handleCancelPendingSite}
          showDisabledHint={editMode && isMachineAssociated}
          disabledHint={disabledHint}
        />
      </Grid.Column>
    </Grid.Row>
  );
}

SiteReassignSection.propTypes = {
  user: PropTypes.shape({}).isRequired,
  currentAccount: PropTypes.shape({
    id: PropTypes.number,
    organisation: PropTypes.shape({
      id: PropTypes.number,
    }),
  }),
  editMode: PropTypes.bool.isRequired,
  getOrganisationSites: PropTypes.func.isRequired,
  transferDeviceToSite: PropTypes.func.isRequired,
  getOrganisationId: PropTypes.func,
  getSiteAccountId: PropTypes.func.isRequired,
  getTransferDeviceParams: PropTypes.func.isRequired,
  hasMachineAssociation: PropTypes.func.isRequired,
  getDisabledHint: PropTypes.func,
  onReassignSuccess: PropTypes.func,
  t: PropTypes.func.isRequired,
};

SiteReassignSection.defaultProps = {
  currentAccount: null,
  getOrganisationId: null,
  getDisabledHint: null,
  onReassignSuccess: null,
};

export default SiteReassignSection;
